import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CalendarResponse } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { calendarApi } from '@/services/calendar.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

/** A fixed hex palette, not a token — a future palette change should not
 *  repaint every church's existing choices out from under them (see
 *  `calendar.prisma`). Matches the backend's `color` validation. */
const PALETTE = [
  '#4F46E5',
  '#0EA5E9',
  '#16A34A',
  '#F97316',
  '#DC2626',
  '#DB2777',
  '#7C3AED',
  '#CA8A04',
  '#0891B2',
  '#64748B',
] as const;

const DEFAULT_COLOR: string = PALETTE[0];

function ColorSwatchPicker({
  value,
  onChange,
}: {
  readonly value: string;
  readonly onChange: (color: string) => void;
}) {
  return (
    <div className="calendar-color-swatches" role="radiogroup">
      {PALETTE.map((color) => (
        <button
          key={color}
          type="button"
          role="radio"
          aria-checked={value === color}
          aria-label={color}
          className="calendar-color-swatch"
          data-selected={value === color || undefined}
          style={{ backgroundColor: color }}
          onClick={() => onChange(color)}
        />
      ))}
    </div>
  );
}

function errorMessage(
  error: unknown,
  t: (key: string, options?: { fallback?: string }) => string,
): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

export function ManageCalendarsDialog({
  calendars,
  onClose,
}: {
  readonly calendars: readonly CalendarResponse[];
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_COLOR);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState(DEFAULT_COLOR);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['calendar'] });

  const createMutation = useMutation({
    mutationFn: () => calendarApi.createCalendar({ name: newName, color: newColor }),
    onSuccess: () => {
      setNewName('');
      setNewColor(DEFAULT_COLOR);
      invalidate();
    },
    onError: (error: unknown) => setFormError(errorMessage(error, t)),
  });

  const updateMutation = useMutation({
    mutationFn: (calendarId: string) =>
      calendarApi.updateCalendar(calendarId, { name: editName, color: editColor }),
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
    onError: (error: unknown) => setFormError(errorMessage(error, t)),
  });

  const archiveMutation = useMutation({
    mutationFn: (calendarId: string) => calendarApi.archiveCalendar(calendarId),
    onSuccess: invalidate,
    onError: (error: unknown) => setFormError(errorMessage(error, t)),
  });

  const startEdit = (calendar: CalendarResponse) => {
    setFormError(null);
    setEditingId(calendar.id);
    setEditName(calendar.name);
    setEditColor(calendar.color);
  };

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog calendar-manage-dialog"
      aria-labelledby="manage-calendars-title"
    >
      <div className="dialog-body">
        <h2 id="manage-calendars-title">{t('calendar.calendar.manage')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <form
          className="calendar-add-form"
          onSubmit={(event) => {
            event.preventDefault();
            setFormError(null);
            createMutation.mutate();
          }}
        >
          <Field
            label={t('calendar.calendar.name')}
            name="name"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            required
          />
          <ColorSwatchPicker value={newColor} onChange={setNewColor} />
          <Button
            type="submit"
            variant="primary"
            disabled={!newName.trim()}
            loading={createMutation.isPending}
          >
            {t('calendar.calendar.add')}
          </Button>
        </form>

        <ul className="calendar-manage-list">
          {calendars.map((calendar) => (
            <li key={calendar.id} className="calendar-manage-row">
              {editingId === calendar.id ? (
                <form
                  className="calendar-edit-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    setFormError(null);
                    updateMutation.mutate(calendar.id);
                  }}
                >
                  <Field
                    label={t('calendar.calendar.name')}
                    name="editName"
                    value={editName}
                    onChange={(event) => setEditName(event.target.value)}
                    required
                    autoFocus
                  />
                  <ColorSwatchPicker value={editColor} onChange={setEditColor} />
                  <div className="calendar-edit-actions">
                    <Button type="button" variant="ghost" onClick={() => setEditingId(null)}>
                      {t('common.actions.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!editName.trim()}
                      loading={updateMutation.isPending}
                    >
                      {t('common.actions.save')}
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  <span
                    className="calendar-toggle-dot"
                    style={{ backgroundColor: calendar.color }}
                  />
                  <span className="calendar-manage-name">{calendar.name}</span>
                  <div className="calendar-manage-actions">
                    <Button type="button" variant="ghost" onClick={() => startEdit(calendar)}>
                      {t('common.actions.edit')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setFormError(null);
                        archiveMutation.mutate(calendar.id);
                      }}
                      loading={
                        archiveMutation.isPending && archiveMutation.variables === calendar.id
                      }
                    >
                      {t('common.actions.archive')}
                    </Button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>

        <footer className="dialog-actions">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.actions.close')}
          </Button>
        </footer>
      </div>
    </dialog>
  );
}
